import React, { useState, useEffect, useRef, useCallback } from "react";
import { Row, Col, Form, Button, Dropdown, Spinner } from "react-bootstrap";
import { Type, Hash } from "react-bootstrap-icons";
import { AnalyticsFilters, AnalyticsFilterOptions, GroupByConfig } from "../../types/analytics";
import { getAnalyticsFilterValues, searchAnalyticsFilters } from "../../utils/Requests";
import { getAnalyticsBrandLabel, getAnalyticsBrandNameLabel } from "../../utils/analytics";
import Logger from "../../utils/Logger";

type AnalyticsFiltersProps = {
    filters: AnalyticsFilters;
    filterOptions: AnalyticsFilterOptions;
    onFilterChange: (filters: AnalyticsFilters) => void;
    onApplyFilters: () => void;
    customerConfig: CustomerConfig;
    /** Enable Group By functionality for specific filter types. Defaults to all disabled. */
    enableGroupBy?: GroupByConfig;
    /** Whether to restrict date pickers to yesterday or earlier. Defaults to true. */
    restrictEndDate?: boolean;
    /** Whether to show a "Today" preset button. Defaults to false. */
    showTodayPreset?: boolean;
    /** Whether filters panel can be collapsed. Defaults to true. */
    collapsible?: boolean;
    /** Initial collapsed state when collapsible is enabled. Defaults to false. */
    defaultCollapsed?: boolean;
    /** Controlled collapsed state for page-level filter controls. */
    isCollapsed?: boolean;
    /** Whether this component manages its own sticky positioning. Defaults to true. */
    sticky?: boolean;
};

type FilterOption = string;

type MultiSelectDropdownProps = {
    label: string;
    selectedValues: string[];
    onChange: (values: string[]) => void;
    placeholder?: string;
    groupField?: string;  // The API field name for grouping (e.g., "device_platform")
    isGrouped?: boolean;  // Whether this field is currently selected for grouping
    onGroupToggle?: (field: string) => void;  // Callback when Group button is clicked
    customerConfig: CustomerConfig;
    filterType: string;  // The filter type for API calls (e.g., "attribution_id")
    /** Whether to show the Group button for this dropdown */
    showGroupButton?: boolean;
};

/**
 * Multi-select dropdown with checkboxes, pagination, and search
 */
const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
    label,
    selectedValues,
    onChange,
    placeholder = "Select...",
    groupField,
    isGrouped = false,
    onGroupToggle,
    customerConfig,
    filterType,
    showGroupButton = false
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [searchTerm, setSearchTerm] = useState("");
    const [options, setOptions] = useState<FilterOption[]>([]);
    const [optionCache, setOptionCache] = useState<Record<string, FilterOption>>({});
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSearching, setIsSearching] = useState(false);
    const [searchById, setSearchById] = useState(false); // false = by name, true = by ID
    const dropdownRef = useRef<HTMLDivElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Determine display label based on filter type and search mode
    const getDisplayLabel = (): string => {
        if (filterType === "brand_id") {
            return searchById ? `${getAnalyticsBrandLabel(customerConfig)} ID` : getAnalyticsBrandNameLabel(customerConfig);
        }
        return label;
    };

    // Determine search placeholder based on filter type and search mode
    const getSearchPlaceholder = (): string => {
        if (filterType === "brand_id") {
            return searchById ? "Search by ID..." : "Search by Name...";
        }
        return "Search...";
    };

    // Helper functions to work with FilterOption
    const getOptionValue = (option: FilterOption): string => {
        return option;
    };

    const getOptionDisplay = (option: FilterOption): string => {
        return option;
    };

    const normalizeFilterOption = useCallback((item: any): FilterOption => {
        if (typeof item === "string") {
            return item;
        }

        if (filterType === "brand_id") {
            return item?.label ? String(item.label) : String(item ?? "");
        }

        if (item?.label !== undefined && item?.label !== null) {
            return String(item.label);
        }

        return String(item ?? "");
    }, [filterType]);

    const mergeIntoOptionCache = useCallback((items: FilterOption[]): void => {
        setOptionCache(prev => {
            const next = { ...prev };
            items.forEach(item => {
                next[item] = item;
            });
            return next;
        });
    }, []);

    const renderHighlightedText = (text: string): React.ReactNode => {
        const normalizedSearchTerm = searchTerm.trim();
        if (!normalizedSearchTerm) {
            return text;
        }

        const escapedSearchTerm = normalizedSearchTerm.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        const searchRegex = new RegExp(`(${escapedSearchTerm})`, "ig");
        const parts = text.split(searchRegex);

        return (
            <>
                {parts.map((part, index) => (
                    part.toLowerCase() === normalizedSearchTerm.toLowerCase()
                        ? <strong key={`${part}-${index}-${normalizedSearchTerm}`} className="analytics-match-highlight">{part}</strong>
                        : <span key={`${part}-${index}`}>{part}</span>
                ))}
            </>
        );
    };

    const findOptionByValue = (value: string): FilterOption | undefined => {
        return options.find(opt => getOptionValue(opt) === value);
    };

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent): void => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setIsOpen(false);
            }
        };

        document.addEventListener("mousedown", handleClickOutside);
        return () => document.removeEventListener("mousedown", handleClickOutside);
    }, []);

    const loadFilterValues = useCallback(async (page: number): Promise<void> => {
        try {
            setIsLoading(true);
            const response = await getAnalyticsFilterValues(customerConfig, filterType, page, searchById);
            
            // Response can be an array of label strings or label objects during rollout.
            const newValues: FilterOption[] = Array.isArray(response)
                ? response.map(item => normalizeFilterOption(item))
                : [];

                        mergeIntoOptionCache(newValues);
            
            if (page === 1) {
                setOptions(newValues);
            } else {
                setOptions(prev => [...prev, ...newValues]);
            }
            
            // If we got 20 items (full page), there might be more
            setHasMore(newValues.length === 20);
            setCurrentPage(page);
        } catch (error) {
            Logger.error("Error loading filter values", error);
        } finally {
            setIsLoading(false);
        }
    }, [customerConfig, filterType, searchById, mergeIntoOptionCache, normalizeFilterOption]);

    const handleSearch = useCallback(async (page = 1): Promise<void> => {
        try {
            setIsSearching(true);
            const response = await searchAnalyticsFilters(
                customerConfig,
                filterType,
                searchTerm,
                searchById,
                page
            );
            
            const results = Array.isArray(response) ? response : response.results;
            const searchResults: FilterOption[] = results.map(item => normalizeFilterOption(item));
            mergeIntoOptionCache(searchResults);

            if (page === 1) {
                setOptions(searchResults);
            } else {
                setOptions(prev => [...prev, ...searchResults]);
            }

            const total = Array.isArray(response) ? undefined : response.total;
            setHasMore(typeof total === "number" && Number.isFinite(total) ? total > page * 20 : searchResults.length === 20);
            setCurrentPage(page);
        } catch (error) {
            Logger.error("Error searching filters", error);
            setOptions([]);
            setHasMore(false);
        } finally {
            setIsSearching(false);
        }
    }, [customerConfig, filterType, searchTerm, searchById, mergeIntoOptionCache, normalizeFilterOption]);

    // Load initial data when dropdown opens or search mode changes
    useEffect(() => {
        if (isOpen && options.length === 0 && !searchTerm) {
            loadFilterValues(1);
        }
    }, [isOpen, options.length, searchTerm, loadFilterValues]);

    // Reload data when search mode changes
    useEffect(() => {
        if (isOpen && filterType === "brand_id") {
            setOptions([]);
            setCurrentPage(1);
            if (searchTerm) {
                handleSearch();
            } else {
                loadFilterValues(1);
            }
        }
    }, [searchById]); // eslint-disable-line react-hooks/exhaustive-deps

    // Handle search with debounce
    useEffect(() => {
        if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
        }

        if (searchTerm) {
            searchTimeoutRef.current = setTimeout(() => {
                handleSearch();
            }, 300);
        } else if (isOpen) {
            // Reset to first page when search is cleared
            setOptions([]);
            setCurrentPage(1);
            loadFilterValues(1);
        }

        return () => {
            if (searchTimeoutRef.current) {
                clearTimeout(searchTimeoutRef.current);
            }
        };
    }, [searchTerm, isOpen, handleSearch, loadFilterValues]);

    const handleLoadMore = (): void => {
        if (!isLoading && !isSearching && hasMore) {
            if (searchTerm) {
                handleSearch(currentPage + 1);
            } else {
                loadFilterValues(currentPage + 1);
            }
        }
    };

    const handleToggle = (value: string): void => {
        if (selectedValues.includes(value)) {
            onChange(selectedValues.filter(v => v !== value));
        } else {
            onChange([...selectedValues, value]);
        }
    };

    const selectedOptions: FilterOption[] = selectedValues.map(value => optionCache[value] ?? value);
    const visibleOptions: FilterOption[] = [
        ...selectedOptions,
        ...options.filter(option => !selectedValues.includes(getOptionValue(option)))
    ];
    const selectedVisibleOptions: FilterOption[] = visibleOptions.filter(option =>
        selectedValues.includes(getOptionValue(option))
    );
    const unselectedVisibleOptions: FilterOption[] = visibleOptions.filter(option =>
        !selectedValues.includes(getOptionValue(option))
    );
    const visibleValues = visibleOptions.map(option => getOptionValue(option));
    const allVisibleSelected = visibleValues.length > 0 && visibleValues.every(value => selectedValues.includes(value));

    const handleSelectAll = (): void => {
        if (allVisibleSelected) {
            onChange(selectedValues.filter(value => !visibleValues.includes(value)));
        } else {
            onChange(Array.from(new Set([...selectedValues, ...visibleValues])));
        }
    };

    const displayText = selectedValues.length === 0
        ? placeholder
        : selectedValues.length === 1
            ? (() => {
                const option = findOptionByValue(selectedValues[0]);
                return option ? getOptionDisplay(option) : selectedValues[0];
            })()
            : `${selectedValues.length} selected`;

    return (
        <div ref={dropdownRef} style={{ position: "relative" }}>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px", minHeight: "24px" }}>
                <Form.Label style={{ marginBottom: 0 }}>{getDisplayLabel()}</Form.Label>
                <div style={{ display: "flex", gap: "4px" }}>
                    {filterType === "brand_id" && (
                        <Button
                            size="sm"
                            variant={"outline-secondary"}
                            onClick={() => setSearchById(!searchById)}
                            style={{
                                padding: "0px 6px",
                                fontSize: "11px",
                                lineHeight: "1.2",
                                height: "20px",
                                backgroundColor: "#e9ecef",
                                borderColor: "#ced4da",
                                color: "#6c757d",
                                display: "flex",
                                alignItems: "center"
                            }}
                            title={searchById ? `Switch to ${getAnalyticsBrandNameLabel(customerConfig)}` : `Switch to ${getAnalyticsBrandLabel(customerConfig)} ID`}
                        >
                            {searchById ? <Hash size={12} /> : <Type size={12} />}
                        </Button>
                    )}
                    {showGroupButton && groupField && onGroupToggle && (
                        <Button
                            size="sm"
                            variant={isGrouped ? "secondary" : "outline-secondary"}
                            onClick={() => onGroupToggle(groupField)}
                            style={{
                                padding: "0px 6px",
                                fontSize: "11px",
                                lineHeight: "1.2",
                                height: "20px",
                                ...(isGrouped ? {} : {
                                    backgroundColor: "#e9ecef",
                                    borderColor: "#ced4da",
                                    color: "#6c757d"
                                })
                            }}
                        >
                            Group
                        </Button>
                    )}
                </div>
            </div>
            <Dropdown show={isOpen} onToggle={() => setIsOpen(!isOpen)}>
                <Dropdown.Toggle
                    variant="outline-secondary"
                    style={{
                        width: "100%",
                        textAlign: "left",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        backgroundColor: "white",
                        color: "black"
                    }}
                >
                    {displayText}
                </Dropdown.Toggle>
                <Dropdown.Menu
                    className="bg-primary-transparent"
                    style={{
                        minWidth: filterType === "brand_id" || filterType === "attribution_id" ? "400px" : "100%",
                        maxWidth: "90vw",
                        maxHeight: "300px",
                        overflowY: "auto",
                        overflowX: "hidden",
                        padding: "8px"
                    }}
                >
                    <Form.Control
                        type="text"
                        placeholder={getSearchPlaceholder()}
                        value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)}
                        style={{ marginBottom: "8px" }}
                        onClick={(e: React.MouseEvent) => e.stopPropagation()}
                    />
                    {(isLoading || isSearching) && options.length === 0 && (
                        <div style={{ padding: "16px", textAlign: "center" }}>
                            <Spinner animation="border" size="sm" />
                        </div>
                    )}
                    {!isLoading && !isSearching && visibleOptions.length > 0 && (
                        <div
                            style={{
                                padding: "4px 8px",
                                borderBottom: "1px solid #eee",
                                marginBottom: "4px"
                            }}
                        >
                            <Form.Check
                                type="checkbox"
                                label={allVisibleSelected ? "Deselect All" : "Select All"}
                                checked={allVisibleSelected}
                                onChange={handleSelectAll}
                                style={{ fontWeight: "bold"}}
                            />
                        </div>
                    )}
                    {!isLoading && !isSearching && visibleOptions.length === 0 && (
                        <div style={{ padding: "8px", color: "#999", textAlign: "center" }}>
                            No options found
                        </div>
                    )}
                    {selectedVisibleOptions.length > 0 && (
                        <div
                            style={{
                                padding: "4px 8px",
                                borderBottom: "1px dashed #d5d5d5",
                                marginBottom: "4px",
                                fontSize: "12px",
                                fontWeight: 600,
                                color: "#6c757d"
                            }}
                        >
                            Selected
                        </div>
                    )}
                    {selectedVisibleOptions.map(option => {
                        const value = getOptionValue(option);
                        const display = getOptionDisplay(option);
                        return (
                            <div
                                key={value}
                                style={{
                                    padding: "4px 8px",
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word"
                                }}
                            >
                                <Form.Check
                                    type="checkbox"
                                    label={renderHighlightedText(display)}
                                    checked={selectedValues.includes(value)}
                                    onChange={() => handleToggle(value)}
                                />
                            </div>
                        );
                    })}
                    {selectedVisibleOptions.length > 0 && unselectedVisibleOptions.length > 0 && (
                        <div
                            style={{
                                borderTop: "1px solid #eee",
                                margin: "4px 0"
                            }}
                        />
                    )}
                    {unselectedVisibleOptions.map(option => {
                        const value = getOptionValue(option);
                        const display = getOptionDisplay(option);
                        return (
                            <div
                                key={value}
                                style={{ 
                                    padding: "4px 8px",
                                    wordBreak: "break-word",
                                    overflowWrap: "break-word"
                                }}
                            >
                                <Form.Check
                                    type="checkbox"
                                    label={renderHighlightedText(display)}
                                    checked={selectedValues.includes(value)}
                                    onChange={() => handleToggle(value)}
                                />
                            </div>
                        );
                    })}
                    {hasMore && (
                        <div
                            style={{
                                padding: "8px",
                                borderTop: "1px solid #eee",
                                marginTop: "4px",
                                textAlign: "center"
                            }}
                        >
                            <Button
                                variant="link"
                                size="sm"
                                onClick={handleLoadMore}
                                disabled={isLoading || isSearching}
                                style={{ padding: "0", textDecoration: "none" }}
                            >
                                {isLoading || isSearching ? (
                                    <>
                                        <Spinner animation="border" size="sm" style={{ marginRight: "8px" }} />
                                        Loading...
                                    </>
                                ) : (
                                    "Load More"
                                )}
                            </Button>
                        </div>
                    )}
                </Dropdown.Menu>
            </Dropdown>
        </div>
    );
};

/**
 * Shared filter component for Analytics page
 * Positioned at top of page with sticky positioning
 */
const AnalyticsFiltersComponent: React.FC<AnalyticsFiltersProps> = ({
    filters,
    filterOptions,
    onFilterChange,
    onApplyFilters,
    customerConfig,
    enableGroupBy = {},
    restrictEndDate = true,
    showTodayPreset = false,
    collapsible = true,
    defaultCollapsed = false,
    isCollapsed: controlledIsCollapsed,
    sticky = true
}) => {
    const [localFilters, setLocalFilters] = useState<AnalyticsFilters>(filters);
    const [appliedFilters, setAppliedFilters] = useState<AnalyticsFilters>(filters);
    const [isScrolled, setIsScrolled] = useState(false);
    const [selectedPreset, setSelectedPreset] = useState<string>("W");
    const [uncontrolledIsCollapsed, setUncontrolledIsCollapsed] = useState<boolean>(collapsible ? defaultCollapsed : false);
    const isCollapsed = collapsible && (controlledIsCollapsed ?? uncontrolledIsCollapsed);

    // Keep filters visible when collapsing is disabled, even if defaultCollapsed is true.
    useEffect(() => {
        if (!collapsible) {
            setUncontrolledIsCollapsed(false);
        }
    }, [collapsible]);

    // Format date as YYYY-MM-DD
    const formatDate = (date: Date): string => {
        const year = date.getFullYear();
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const day = String(date.getDate()).padStart(2, "0");
        return `${year}-${month}-${day}`;
    };

    // Handle date preset selection
    const handleDatePreset = (preset: string): void => {
        const today = new Date();
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        // Pages with PoP (restrictEndDate=true) use yesterday as the reference end date;
        // pages without PoP (restrictEndDate=false) use today.
        const referenceEnd = restrictEndDate ? yesterday : today;
        let startDate: Date;
        let endDate: Date = referenceEnd;

        switch (preset) {
            case "T": // Today
                startDate = today;
                endDate = today;
                break;
            case "3": // Last 3 days
                startDate = new Date(referenceEnd);
                startDate.setDate(referenceEnd.getDate() - 2);
                break;
            case "W": // Week
                startDate = new Date(referenceEnd);
                startDate.setDate(referenceEnd.getDate() - 6);
                break;
            case "M": // Month
                startDate = new Date(referenceEnd);
                startDate.setMonth(referenceEnd.getMonth() - 1);
                break;
            case "LM": // Last Month
                startDate = new Date(today.getFullYear(), today.getMonth() - 1, 1);
                endDate = new Date(today.getFullYear(), today.getMonth(), 0);
                break;
            case "Y": // Year
                startDate = new Date(referenceEnd);
                startDate.setDate(referenceEnd.getDate() - 365);
                break;
            default:
                return;
        }

        const updated = {
            ...localFilters,
            startDate: formatDate(startDate),
            endDate: formatDate(endDate)
        };
        setLocalFilters(updated);
        onFilterChange(updated);
        setSelectedPreset(preset);
    };

    useEffect(() => {
        const handleScroll = (): void => {
            setIsScrolled(window.scrollY > 10);
        };

        window.addEventListener("scroll", handleScroll);
        return () => window.removeEventListener("scroll", handleScroll);
    }, []);

    // Set default preset on mount
    useEffect(() => {
        handleDatePreset("W");
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // Check if filters have changed from the last applied state
    const hasChanges = (): boolean => {
        return (
            localFilters.startDate !== appliedFilters.startDate ||
            localFilters.endDate !== appliedFilters.endDate ||
            JSON.stringify(localFilters.devicePlatform) !== JSON.stringify(appliedFilters.devicePlatform) ||
            JSON.stringify(localFilters.brandName) !== JSON.stringify(appliedFilters.brandName) ||
            JSON.stringify(localFilters.attributionId) !== JSON.stringify(appliedFilters.attributionId) ||
            localFilters.focusField !== appliedFilters.focusField ||
            localFilters.focusSize !== appliedFilters.focusSize
        );
    };

    const handleDateChange = (field: "startDate" | "endDate") => (
        event: React.ChangeEvent<HTMLInputElement>
    ): void => {
        const updated = { ...localFilters, [field]: event.target.value };
        setLocalFilters(updated);
        setSelectedPreset("");
        onFilterChange(updated);
    };

    const handleMultiSelectChange = (field: "attributionId" | "brandName" | "devicePlatform") => (
        values: string[]
    ): void => {
        const updated = { ...localFilters, [field]: values };
        setLocalFilters(updated);
        onFilterChange(updated);
    };

    /**
     * Optimizes filters by converting "all selected" to empty array.
     * Note: Since filters are now loaded dynamically with pagination,
     * we cannot determine if "all" are selected, so we pass the selections as-is.
     */
    const getOptimizedFilters = (): AnalyticsFilters => {
        return {
            ...localFilters,
            focusField: localFilters.focusField,
            focusSize: localFilters.focusSize
        };
    };

    const handleGroupToggle = (field: string): void => {
        // Toggle: if already selected, deselect; otherwise select this field
        const newValue = localFilters.focusField === field ? undefined : field;
        const updated = { ...localFilters, focusField: newValue };
        setLocalFilters(updated);
        onFilterChange(updated);
    };

    const handleApply = (): void => {
        const optimizedFilters = getOptimizedFilters();
        setAppliedFilters(localFilters);
        onFilterChange(optimizedFilters);
        onApplyFilters();
    };

    return (
        <div
            className={isScrolled ? "bg-primary-transparent" : ""}
            style={{
                position: sticky ? "sticky" : "static",
                top: sticky ? 56 : undefined,
                zIndex: 100,
                backgroundColor: isScrolled ? undefined : "transparent",
                paddingTop: "12px",
                paddingBottom: "8px",
                marginBottom: "2px",
                marginLeft: "-15px",
                marginRight: "-15px",
                paddingLeft: "15px",
                paddingRight: "15px",
                width: "calc(100% + 30px)",
                transition: "background-color 0.2s ease"
            }}
        >
            {!isCollapsed && (
                <Row className="align-items-end">
                <Col md={2}>
                    <Form.Group>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: "4px", minHeight: "24px" }}>
                            <Form.Label style={{ marginBottom: 0 }}>Start Date (UTC)</Form.Label>
                            <div style={{ display: "flex", gap: "4px" }}>
                                {(showTodayPreset ? ["T", "3", "W", "M", "LM", "Y"] : ["3", "W", "M", "LM", "Y"]).map(preset => (
                                    <Button
                                        key={preset}
                                        size="sm"
                                        variant={selectedPreset === preset ? "secondary" : "outline-secondary"}
                                        onClick={() => handleDatePreset(preset)}
                                        style={{
                                            padding: "0px 5px",
                                            fontSize: "10px",
                                            lineHeight: "1.2",
                                            height: "20px",
                                            minWidth: "20px",
                                            ...(!selectedPreset || selectedPreset !== preset ? {
                                                backgroundColor: "#e9ecef",
                                                borderColor: "#ced4da",
                                                color: "#6c757d"
                                            } : {})
                                        }}
                                        title={
                                            preset === "T" ? "Today" :
                                            preset === "3" ? "Last 3 Days" :
                                            preset === "W" ? "Week" :
                                            preset === "M" ? "Month" :
                                            preset === "LM" ? "Last Month" :
                                            "Year"
                                        }
                                    >
                                        {preset}
                                    </Button>
                                ))}
                            </div>
                        </div>
                        <Form.Control
                            type="date"
                            value={localFilters.startDate}
                            max={restrictEndDate ? formatDate(new Date(new Date().getTime() - 86400000)) : undefined}
                            onChange={handleDateChange("startDate")}
                            style={{ border: "1px solid #ced4da" }}
                        />
                    </Form.Group>
                </Col>
                <Col md={2}>
                    <Form.Group>
                        <Form.Label style={{ marginBottom: "4px", minHeight: "24px", display: "flex", alignItems: "center" }}>End Date (UTC)</Form.Label>
                        <div title={restrictEndDate ? "Date selection is only possible for a full day's data." : undefined}>
                            <Form.Control
                                type="date"
                                value={localFilters.endDate}
                                max={restrictEndDate ? formatDate(new Date(new Date().getTime() - 86400000)) : undefined}
                                onChange={handleDateChange("endDate")}
                                style={{ border: "1px solid #ced4da" }}
                            />
                        </div>
                    </Form.Group>
                </Col>
                <Col md={2}>
                    <Form.Group>
                        <MultiSelectDropdown
                            label="Device Platform"
                            selectedValues={localFilters.devicePlatform}
                            onChange={handleMultiSelectChange("devicePlatform")}
                            placeholder="All devices"
                            groupField="device_platform"
                            isGrouped={localFilters.focusField === "device_platform"}
                            onGroupToggle={handleGroupToggle}
                            customerConfig={customerConfig}
                            filterType="device_platform"
                            showGroupButton={enableGroupBy.devicePlatform === true}
                        />
                    </Form.Group>
                </Col>
                <Col md={2}>
                    <Form.Group>
                        <MultiSelectDropdown
                            label={getAnalyticsBrandNameLabel(customerConfig)}
                            selectedValues={localFilters.brandName}
                            onChange={handleMultiSelectChange("brandName")}
                            placeholder={`All ${getAnalyticsBrandLabel(customerConfig, true).toLowerCase()}`}
                            groupField="brand_name"
                            isGrouped={localFilters.focusField === "brand_name"}
                            onGroupToggle={handleGroupToggle}
                            customerConfig={customerConfig}
                            filterType="brand_id"
                            showGroupButton={enableGroupBy.brandName === true}
                        />
                    </Form.Group>
                </Col>
                <Col md={2}>
                    <Form.Group>
                        <MultiSelectDropdown
                            label="Attribution ID"
                            selectedValues={localFilters.attributionId}
                            onChange={handleMultiSelectChange("attributionId")}
                            placeholder="All attributions"
                            groupField="attribution_id"
                            isGrouped={localFilters.focusField === "attribution_id"}
                            onGroupToggle={handleGroupToggle}
                            customerConfig={customerConfig}
                            filterType="attribution_id"
                            showGroupButton={enableGroupBy.attributionId === true}
                        />
                    </Form.Group>
                </Col>
                <Col md={2}>
                    <Form.Group>
                        <Form.Label style={{ marginBottom: "4px", minHeight: "24px", display: "flex", alignItems: "center" }}>&nbsp;</Form.Label>
                        <Button
                            variant={hasChanges() ? "secondary" : "outline-secondary"}
                            onClick={handleApply}
                            disabled={!hasChanges()}
                            style={{
                                width: "100%",
                                display: "block",
                                ...(!hasChanges() && {
                                    backgroundColor: "#e9ecef",
                                    borderColor: "#ced4da",
                                    color: "#6c757d",
                                    cursor: "not-allowed"
                                })
                            }}
                        >
                            Apply Filters
                        </Button>
                    </Form.Group>
                </Col>
                </Row>
            )}
        </div>
    );
};

export default AnalyticsFiltersComponent;
